import { Tooltip } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { ServerDomainInfo } from '@/api/type-server'
// import Check from '@/assets/icons/check.svg?react'
// import Cross from '@/assets/icons/cross.svg?react'
import InfoIcon from '@/assets/icons/info-i.svg?react'
import { Button, ButtonRadio, Divider, Loading, Pagination, Trans } from '@/components'
import { ActionMenu } from '@/components/base/ActionMenu'
import { Dropdown } from '@/components/base/Dropdown'
import { CollapseTableRow } from '@/components/CollapseTableRow'
import { EmptyView } from '@/components/EmptyView'
import { FilterItem, showSnapshotBtnMap } from '@/hooks/useInstanceList'
import { Instance } from '@/models/instance'
import { CreateSnapshotModal } from '@/pages/instance-list/components/CreateSnapshotModal'
import { EditableInstanceName } from '@/pages/instance-list/components/EditableInstanceName'
import { InstanceDetail } from '@/pages/instance-list/components/InstanceDetail'
import { RenewInstanceModal } from '@/pages/instance-list/components/RenewInstanceModal'
import { formatDatePreset, formatDomain, formatPrice } from '@/utils/formatter'
import { cn } from '@/utils/style'

interface InstanceListViewProps {
  // States
  filter: FilterItem | undefined
  setFilter: (filter: FilterItem | undefined) => void
  page: number
  setPage: (page: number) => void
  total: number
  instaceList: Instance[]
  loading: boolean
  domainInfos: ServerDomainInfo[]
  setRegions: (regions: number[]) => void
  regionFilterCriteria: number
  setRegionFilterCriteria: (criteria: number) => void
  createSnapshotInstance: Instance | undefined
  setCreateSnapshotInstance: (instance: Instance | undefined) => void

  // Methods
  getNodeList: (page: number, force: boolean) => Promise<void>
  onRelease: (data: Instance) => Promise<void>
  onCancelSnapshot?: (data: Instance) => Promise<void>
  onUpdate: () => void

  // Constants
  statusFilters: FilterItem[]
  pageSize: number
  REGION_GLOBAL: number
  REGION_DATA_DRIVE: number
  REGION_MESH_CLUSTERING: number

  // Optional: hide header section (for parent-controlled layout)
  hideHeader?: boolean

  snapshotOnSpaceBilling?: boolean

  // Optional: custom detail component and its props
  DetailComponent?: React.ComponentType<any>
  detailComponentProps?: Record<string, any>
}

export function InstanceListView({
  filter,
  setFilter,
  page,
  total,
  instaceList,
  loading,
  domainInfos,
  setRegions,
  regionFilterCriteria,
  setRegionFilterCriteria,
  createSnapshotInstance,
  setCreateSnapshotInstance,
  getNodeList,
  onRelease,
  onCancelSnapshot,
  onUpdate,
  statusFilters,
  pageSize,
  REGION_GLOBAL,
  REGION_DATA_DRIVE,
  REGION_MESH_CLUSTERING,
  hideHeader = false,
  snapshotOnSpaceBilling = true,
  DetailComponent,
  detailComponentProps,
}: InstanceListViewProps) {
  const { t } = useTranslation()
  const location = useLocation()
  const expandInstanceId = new URLSearchParams(location.search).get('expand')
  const [renewInstance, setRenewInstance] = useState<Instance | undefined>(undefined)

  // Determine create path based on current route context
  const isTeamContext = location.pathname.startsWith('/team')
  const createPath = isTeamContext ? '/team/create' : '/create'

  return (
    <div
      className={cn(
        { 'card bg-panel-white min-h-full': !isTeamContext },
        { 'px-0.5': isTeamContext },
        'overflow-y-auto',
      )}
    >
      {!hideHeader && (
        <>
          <div className="flex h-[60px] items-center justify-between">
            <div className="content-stretch flex gap-2 items-center">
              <div className="h6">
                <Trans i18nKey="instance.title">My Instances</Trans>
              </div>
            </div>
            <Link to={createPath}>
              <Button type="secondary">
                <Trans i18nKey="instance.createBtn">Create New</Trans>
              </Button>
            </Link>
          </div>

          <Divider />
        </>
      )}

      <div className="flex gap-8 mb-4">
        <div className="flex flex-col gap-2">
          <div className="text-secondary t-sb">
            <Trans i18nKey="instance.list.statusLabel">Instance Status</Trans>
          </div>
          <div>
            <ButtonRadio
              value={filter?.value}
              items={statusFilters}
              onChange={(e) => {
                setFilter(statusFilters.find((i) => i.value === e))
              }}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-secondary t-sb">
            <Trans i18nKey="create.regionLabel">Region</Trans>
          </div>
          <div>
            <Dropdown
              rootClassName="t-sb w-60"
              className="h-11"
              options={[
                { label: t('common.region.groupGlobal'), value: REGION_GLOBAL, className: 't-sm' },
                {
                  label: t('common.region.groupDataDrive'),
                  value: REGION_DATA_DRIVE,
                  className: 't-sm',
                },
                {
                  label: t('common.region.groupMeshClustering'),
                  value: REGION_MESH_CLUSTERING,
                  className: 't-sm',
                },
                ...domainInfos.map((info, index) => ({
                  label: info.displayName || formatDomain(info.domain),
                  value: info.domain,
                  className: 't-sm',
                  separatorBefore: index === 0,
                })),
              ]}
              value={regionFilterCriteria}
              onChange={(value) => {
                const numValue = Number(value)
                setRegionFilterCriteria(numValue)

                if (numValue === REGION_GLOBAL) {
                  setRegions(domainInfos.map((info) => info.domain))
                } else if (numValue === REGION_DATA_DRIVE || numValue === REGION_MESH_CLUSTERING) {
                  setRegions([])
                } else {
                  setRegions([numValue])
                }
              }}
            />
          </div>
        </div>
      </div>

      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!loading && instaceList.length === 0 && (
        <div className="mt-4">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        </div>
      )}
      {!loading && instaceList.length > 0 && (
        <table className="table-card mt-4 table-fixed">
          <thead>
            <tr>
              <th colSpan={1}>
                <Trans i18nKey="instance.tableHeaderID">ID</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="instance.tableHeaderName">Name</Trans>
              </th>
              <th colSpan={1}>
                <Trans i18nKey="instance.tableHeaderRegion">Region</Trans>
              </th>
              <th colSpan={1}>
                <Trans i18nKey="instance.tableHeaderStatus">Status</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="instance.tableHeaderBillingMethod">Billing Method</Trans>
              </th>
              <th colSpan={1}>
                <Trans i18nKey="instance.tableHeaderCost">Cost</Trans>
              </th>
              {filter?.value !== 'released' && (
                <th colSpan={2}>
                  <Trans i18nKey="instance.tableHeaderAction">Action</Trans>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {instaceList.map((data) => (
              <CollapseTableRow
                key={data.id}
                colSpan={filter?.value === 'released' ? 9 : 10}
                tableClassName={'!border-none ring-1 ring-neutral-200 rounded-xl group'}
                defaultExpanded={data.resID === expandInstanceId}
                content={
                  DetailComponent ? (
                    <DetailComponent
                      instance={data}
                      onUpdate={onUpdate}
                      {...detailComponentProps}
                    />
                  ) : (
                    <InstanceDetail instance={data} onUpdate={onUpdate} />
                  )
                }
              >
                <td className="t-sm whitespace-nowrap" colSpan={1}>
                  {data.resID}
                </td>
                <td colSpan={2}>
                  <div className="flex items-center group">
                    <EditableInstanceName instance={data} onUpdate={onUpdate} />
                  </div>
                </td>
                <td className="t-sm text-secondary" colSpan={1}>
                  {domainInfos.find((info) => info.domain === data.domain)?.displayName ||
                    formatDomain(data.domain)}
                </td>
                <td className="t-sm" colSpan={1}>
                  {data.progress !== undefined ? (
                    <div className="flex flex-col gap-1">
                      <span className="text-btn-secondary">
                        {t(data.statusKey, data.statusText)} ({Math.round(data.progress * 100)}%)
                      </span>
                      <div className="w-full h-1 bg-neutral-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all"
                          style={{ width: `${data.progress * 100}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <span className={data.statusTextStyle}>
                      {t(data.statusKey, data.statusText)}
                    </span>
                  )}
                </td>
                <td colSpan={2}>
                  <div className="flex flex-row items-center gap-1.5">
                    <span className="t-sm">{data.billingMethodLabel}</span>
                    {data.isSubscription && data.subscriptionDeadlineTime && (
                      <Tooltip
                        title={
                          data.autoRenewal
                            ? `Next Renewal Time ${formatDatePreset(data.subscriptionDeadlineTime, 'second')}`
                            : `Expires on ${formatDatePreset(data.subscriptionDeadlineTime, 'second')}`
                        }
                        placement="top"
                        trigger={['hover', 'click']}
                      >
                        <InfoIcon className="w-4 h-4 cursor-pointer text-secondary" />
                      </Tooltip>
                    )}
                    {data.isSubscription && data.autoRenewal && (
                      <div
                        className="inline-flex items-center justify-center px-1.5 rounded flex-shrink-0 whitespace-nowrap"
                        style={{
                          background: 'linear-gradient(90deg, #C0D5FF 0%, #335CFF 100%)',
                          width: 'fit-content',
                        }}
                      >
                        <span className="text-white font-bold text-[10px] whitespace-nowrap">
                          Auto Renewal
                        </span>
                      </div>
                    )}
                  </div>
                </td>
                <td colSpan={1}>{formatPrice(data.cost, false)}</td>
                {filter?.value !== 'released' && (
                  <td colSpan={1}>
                    <div className="flex items-center justify-start gap-6">
                      {data.canRelease && !data.isSubscription && (
                        <Button
                          type="link"
                          className="p-0 text-highlight"
                          onClick={() => onRelease(data)}
                        >
                          <Trans i18nKey="instance.list.action.release">Release</Trans>
                        </Button>
                      )}

                      <ActionMenu
                        items={[
                          {
                            label: (
                              <Trans i18nKey="instance.list.action.takeSnapshot">
                                Take Snapshot
                              </Trans>
                            ),
                            onClick: () => setCreateSnapshotInstance(data),
                            show:
                              data.canSnapshot && (showSnapshotBtnMap[data.node.image.id] ?? true),
                          },
                          {
                            label: (
                              <Trans i18nKey="instance.list.action.cancelSnapshot">
                                Cancel Snapshot
                              </Trans>
                            ),
                            onClick: () => onCancelSnapshot?.(data),
                            show: data.canCancelSnapshot && !!onCancelSnapshot,
                          },
                          {
                            label: data.order ? (
                              <Trans i18nKey="instance.list.action.renew">Renew</Trans>
                            ) : (
                              <Trans i18nKey="instance.list.action.billingMethod">
                                Switch to Subscription
                              </Trans>
                            ),
                            onClick: () => setRenewInstance(data),
                            show: data.canRelease,
                          },
                          // {
                          //   label: (
                          //     <Trans i18nKey="instance.list.action.autoRenewal">Auto-Renewal</Trans>
                          //   ),
                          //   onClick: () => {},
                          //   show: data.isSubscription && data.canRelease,
                          //   dividerBefore: true,
                          //   rightIcon: data.autoRenewal ? (
                          //     <Check className="w-4 h-4" />
                          //   ) : (
                          //     <Cross className="w-4 h-4" />
                          //   ),
                          // },
                        ].filter((item) => item.show !== false)}
                        popupContainer="body"
                      />
                    </div>
                  </td>
                )}
              </CollapseTableRow>
            ))}
          </tbody>
        </table>
      )}

      {total > 0 && !loading && (
        <Pagination
          size={pageSize}
          total={total}
          current={page}
          className="justify-end mt-4"
          onChange={(page) => getNodeList(page, true)}
        />
      )}

      {createSnapshotInstance ? (
        <CreateSnapshotModal
          onClose={() => setCreateSnapshotInstance(undefined)}
          instance={createSnapshotInstance}
          snapshotOnSpaceBilling={snapshotOnSpaceBilling}
        />
      ) : null}

      {renewInstance ? (
        <RenewInstanceModal instance={renewInstance} onClose={() => setRenewInstance(undefined)} />
      ) : null}
    </div>
  )
}
